<template>
	<div id="lta-sales-master-report">
		<p-card :class="{ collapsed: !expanded }">
			<template #header>
				<div class="flex align-items-center justify-content-between h-full" @click="expanded = !expanded">
					<h2>Live Transfer Agents</h2>
					<div class="expander">
						<icon type="chevron-down" size="24px" />
					</div>
				</div>
			</template>
			<template #content>
				<p-data-table
					ref="lta_master_report"
					:value="reportData.data.length > 0 ? [{}] : []"
					:loading="loading"
					data-key="agent_id"
					row-group-mode="subheader"
					group-rows-by="agent_id"
					class="master-report-table"
					scrollable
				>
					<template #empty>
						<div class="dim">No Data</div>
					</template>
					<template #loading>
						<line-loader :show="loading" />
					</template>
					<template #groupheader="row">
						{{ row.data.account_manager_name }}
					</template>
					<p-column header="Metric" frozen>
						<template #body="row">
							<div class="data-cell group">Revenue &amp; Transfers</div>
							<div class="data-cell">Revenue</div>
							<div class="data-cell">Health Transfers</div>
							<div class="data-cell">Medicare Transfers</div>
							<div class="data-cell">Total Transfers</div>
							<div class="data-cell">Failed Transfers</div>
							<div class="data-cell group">Transfer Rate</div>
							<div class="data-cell">Leads</div>
							<div class="data-cell">Transfer Rate</div>
							<div class="data-cell">Transfers / LTA</div>
							<div class="data-cell group">LTAs</div>
							<div class="data-cell">Team Size</div>
							<div class="data-cell tall">Top LTAs</div>
						</template>
					</p-column>
					<p-column :header="aggregateType" frozen>
						<template #body="row">
							<div class="data-cell group">&nbsp;</div>
							<div class="data-cell">{{ currency(reportData[aggregate].revenue) }}</div>
							<div class="data-cell">{{ numberFormat(reportData[aggregate].health_xfrs) || 0 }}</div>
							<div class="data-cell">{{ numberFormat(reportData[aggregate].medicare_xfrs) || 0 }}</div>
							<div class="data-cell">{{ numberFormat(reportData[aggregate].total_xfrs) || 0 }}</div>
							<div class="data-cell">{{ numberFormat(reportData[aggregate].failed_xfrs) || 0 }}</div>
							<div class="data-cell group">&nbsp;</div>
							<div class="data-cell">{{ numberFormat(reportData[aggregate].leads) || 0 }}</div>
							<div class="data-cell">{{ percentage(reportData[aggregate].xfr_rate || 0) }}</div>
							<div class="data-cell">{{ reportData[aggregate].xfrs_per_lta || 0 }}</div>
							<div class="data-cell group">&nbsp;</div>
							<div class="data-cell">{{ reportData[aggregate].team_size || 0 }}</div>
							<div class="data-cell tall">
								<template v-if="reportData[aggregate].top_agents.length">
									<ol>
										<li v-for="agent in reportData[aggregate].top_agents">{{ agent }}</li>
									</ol>
								</template>
								<template v-else> NA </template>
							</div>
						</template>
					</p-column>
					<p-column v-for="date in reportData.data" :header="date.grouping">
						<template #body>
							<div class="data-cell group">&nbsp;</div>
							<div
								:class="[
									'data-cell',
									colorCell(date.revenue, reportData.ranges.revenue[0], reportData.ranges.revenue[1]),
								]"
							>
								{{ currency(date.revenue) }}
							</div>
							<div
								:class="[
									'data-cell',
									colorCell(date.health_xfrs, reportData.ranges.health_xfrs[0], reportData.ranges.health_xfrs[1]),
								]"
							>
								{{ date.health_xfrs || 0 }}
							</div>
							<div
								:class="[
									'data-cell',
									colorCell(date.medicare_xfrs, reportData.ranges.medicare_xfrs[0], reportData.ranges.medicare_xfrs[1]),
								]"
							>
								{{ date.medicare_xfrs || 0 }}
							</div>
							<div
								:class="[
									'data-cell',
									colorCell(date.total_xfrs, reportData.ranges.total_xfrs[0], reportData.ranges.total_xfrs[1]),
								]"
							>
								{{ date.total_xfrs || 0 }}
							</div>
							<div
								:class="[
									'data-cell',
									colorCell(date.failed_xfrs, reportData.ranges.failed_xfrs[0], reportData.ranges.failed_xfrs[1]),
								]"
							>
								{{ date.failed_xfrs || 0 }}
							</div>
							<div class="data-cell group">&nbsp;</div>
							<div
								:class="['data-cell', colorCell(date.leads, reportData.ranges.leads[0], reportData.ranges.leads[1])]"
							>
								{{ numberFormat(date.leads) || 0 }}
							</div>
							<div
								:class="[
									'data-cell',
									colorCell(date.xfr_rate, reportData.ranges.xfr_rate[0], reportData.ranges.xfr_rate[1]),
								]"
							>
								{{ percentage(date.xfr_rate || 0, 2) }}
							</div>
							<div
								:class="[
									'data-cell',
									colorCell(date.xfrs_per_lta, reportData.ranges.xfrs_per_lta[0], reportData.ranges.xfrs_per_lta[1]),
								]"
							>
								{{ date.xfrs_per_lta || 0 }}
							</div>
							<div class="data-cell group">&nbsp;</div>
							<div
								:class="['data-cell', colorCell(date.agents, reportData.ranges.agents[0], reportData.ranges.agents[1])]"
							>
								{{ date.agents || 0 }}
							</div>
							<div class="data-cell tall">
								<template v-if="Array.isArray(date.top_agents) && date.top_agents.length">
									<ol>
										<li v-for="agent in date.top_agents">{{ agent }}</li>
									</ol>
								</template>
								<template v-else> NA </template>
							</div>
						</template>
					</p-column>
					<p-column class="width-hack"></p-column>
				</p-data-table>
			</template>
		</p-card>
	</div>
</template>

<script>
import { colorCell, currency, numberFormat, percentage } from '@/lib/Filters';

export default {
	name: 'LTASalesMasterReport',
	props: {
		aggregate: {
			type: String,
			default: 'totals',
		},
		rawReportData: {
			type: Object,
			default() {
				return {};
			},
		},
		loading: Boolean,
	},
	data() {
		return {
			expanded: true,
		};
	},
	computed: {
		aggregateType() {
			if (this.aggregate === 'totals') {
				return 'Total';
			}
			return 'Average';
		},
		reportData() {
			return this.rawReportData;
		},
		dateRange() {
			if (this.reportData.length > 0) {
				return Object.keys(this.reportData[0].data);
			}
			return [];
		},
	},
	methods: {
		colorCell,
		currency,
		numberFormat,
		percentage,
	},
};
</script>

<style scoped lang="less">
:deep(.p-card-body) {
	padding: 0;
}

.data-cell.group {
	background-color: var(--gray-90);
	color: white;
}

.data-cell.tall {
	min-height: 85px;
}
</style>
